import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PRODUCT_TYPES } from '../constants';
import { formatPrice } from './index';
import { calculateCustomerGrandTotal } from './customerFinancialUtils';

// Ana Renk - Bronz/Kahve
const PRIMARY_COLOR = [176, 67, 9]; // #b04309
const SECONDARY_COLOR = [143, 53, 14]; // #8f350e
const LIGHT_BG = [254, 247, 237]; // #fef7ed
const GRAY_TEXT = [100, 100, 100];

// Türkçe karakter temizleme fonksiyonu
const cleanTurkishChars = (text) => {
  return (text || '').replace(/[ğ]/g, 'g')
    .replace(/[Ğ]/g, 'G')
    .replace(/[ü]/g, 'u')
    .replace(/[Ü]/g, 'U')
    .replace(/[ş]/g, 's')
    .replace(/[Ş]/g, 'S')
    .replace(/[ı]/g, 'i')
    .replace(/[İ]/g, 'I')
    .replace(/[ö]/g, 'o')
    .replace(/[Ö]/g, 'O')
    .replace(/[ç]/g, 'c')
    .replace(/[Ç]/g, 'C');
};

// Modern Header çiz
const drawHeader = (doc, title, subTitle = '') => {
  // Üst banner
  doc.setFillColor(...PRIMARY_COLOR);
  doc.rect(0, 0, 210, 35, 'F');

  // Firma adı
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('EVAX PERDE', 20, 18);

  // Alt başlık / Müşteri Adı
  if (subTitle) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(cleanTurkishChars(subTitle).toUpperCase(), 20, 29);
  } else {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(cleanTurkishChars(title), 20, 28);
  }

  // Sağ taraf - İletişim
  doc.setFontSize(9);
  doc.text('Tel: +90 553 812 04 51', 190, 14, { align: 'right' });
  doc.text('evaxperde@gmail.com', 190, 22, { align: 'right' });
  doc.text('Istanbul, Turkiye', 190, 30, { align: 'right' });

  // Reset text color
  doc.setTextColor(0, 0, 0);
};

// Modern Footer çiz
const drawFooter = (doc) => {
  const pageHeight = doc.internal.pageSize.height;

  // Alt çizgi
  doc.setDrawColor(...PRIMARY_COLOR);
  doc.setLineWidth(0.5);
  doc.line(20, pageHeight - 25, 190, pageHeight - 25);

  // Footer text
  doc.setFontSize(8);
  doc.setTextColor(...GRAY_TEXT);
  doc.text('Evax Perde - Kaliteli Perde Cozumleri', 105, pageHeight - 18, { align: 'center' });
  doc.text('Bu belge bilgisayar ortaminda olusturulmustur.', 105, pageHeight - 12, { align: 'center' });

  // Sayfa numarası
  const pageCount = doc.internal.getNumberOfPages();
  doc.text(`Sayfa ${doc.internal.getCurrentPageInfo().pageNumber} / ${pageCount}`, 190, pageHeight - 12, { align: 'right' });

  doc.setTextColor(0, 0, 0);
};

// Metni satırlara böl
const splitTextToLines = (text, maxCharsPerLine) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach(word => {
    if ((currentLine + ' ' + word).trim().length <= maxCharsPerLine) {
      currentLine = (currentLine + ' ' + word).trim();
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });
  if (currentLine) lines.push(currentLine);
  return lines;
};

// Müşteri bilgileri kutusu
const drawCustomerInfoBox = (doc, customerInfo, startY) => {
  const cleanName = cleanTurkishChars(customerInfo.name || '');
  const cleanAddress = cleanTurkishChars(customerInfo.address || '');

  // Adresi satırlara böl (maks 60 karakter per satır)
  const addressLines = splitTextToLines(cleanAddress, 60);
  const extraLines = Math.max(0, addressLines.length - 1);
  const boxHeight = 45 + (extraLines * 7);

  // Kutu arka planı
  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(15, startY, 180, boxHeight, 3, 3, 'F');

  // Sol taraf - Müşteri bilgileri
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('MUSTERI BILGILERI', 20, startY + 10);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(`Ad Soyad: ${cleanName}`, 20, startY + 20);
  doc.text(`Telefon: ${customerInfo.phone1 || '-'}`, 20, startY + 28);
  
  if (customerInfo.tcNo) {
    doc.text(`TC / Vergi No: ${customerInfo.tcNo}`, 100, startY + 28);
  }

  // Adres satırları
  let addressY = startY + 36;
  addressLines.forEach((line, index) => {
    const prefix = index === 0 ? 'Adres: ' : '       ';
    doc.text(`${prefix}${line}`, 20, addressY);
    addressY += 7;
  });

  // Sağ taraf - Tarihler
  if (customerInfo.orderDate || customerInfo.deliveryDate) {
    doc.setFontSize(9);
    doc.setTextColor(...GRAY_TEXT);

    if (customerInfo.orderDate) {
      const orderDate = customerInfo.orderDate?.toDate ? customerInfo.orderDate.toDate() : new Date(customerInfo.orderDate);
      doc.text(`Siparis: ${orderDate.toLocaleDateString('tr-TR')}`, 190, startY + 20, { align: 'right' });
    }

    if (customerInfo.deliveryDate) {
      const deliveryDate = customerInfo.deliveryDate?.toDate ? customerInfo.deliveryDate.toDate() : new Date(customerInfo.deliveryDate);
      doc.text(`Teslimat: ${deliveryDate.toLocaleDateString('tr-TR')}`, 190, startY + 28, { align: 'right' });
    }
  }

  doc.setTextColor(0, 0, 0);
  return startY + boxHeight + 8;
};

// Toplam tutar kutusu - Sağda alt alta format
const drawTotalBox = (doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, startY, showPaymentInfo = false, paidAmount = 0, remainingAmount = 0, showDetailedPricing = true, customerInfo = {}, orders = []) => {
  const vYuzde = parseFloat(vadeliYuzde) || 0;
  const gIskonto = parseFloat(genelIskontoYuzde) || 0;

  const hasManualOverride = customerInfo?.manualTotalOverride != null && customerInfo?.manualTotalOverride !== '';
  const hasVade = vYuzde > 0 && !hasManualOverride;
  const hasIskonto = gIskonto > 0 && !hasManualOverride;
  const hasProductDiscount = discountedTotalAmount !== totalAmount && discountedTotalAmount > 0;

  const vadeFarkiTutari = totalAmount * vYuzde / 100;
  const vadeliToplam = hasVade ? totalAmount + vadeFarkiTutari : totalAmount;
  const iskontoluToplam = totalAmount - (totalAmount * gIskonto / 100);

  const grandTotal = calculateCustomerGrandTotal(
    { ...customerInfo, vadeliYuzde, genelIskontoYuzde },
    orders
  );

  // Kutu yüksekliğini hesapla
  let itemCount = 1; // Sadece Genel toplam (alt sınır)
  if (showDetailedPricing) {
    itemCount += 1; // Ara toplam
    if (hasProductDiscount) itemCount++;
    if (hasIskonto) itemCount += 2; // % + tutar
    if (hasVade) itemCount += 2; // % + tutar
  }

  if (showPaymentInfo) {
    itemCount += 2; // Ödenen + Kalan
  }
  const boxHeight = 20 + (itemCount * 10);

  // Ana kutu
  doc.setFillColor(...PRIMARY_COLOR);
  doc.roundedRect(100, startY, 95, boxHeight, 3, 3, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  let lineY = startY + 12;

  if (showDetailedPricing) {
    // Ara Toplam (Brüt)
    doc.text('Ara Toplam:', 105, lineY);
    doc.text(`${formatPrice(totalAmount)} TL`, 190, lineY, { align: 'right' });
    lineY += 9;

    // Ürün İndirimli Toplam
    if (hasProductDiscount) {
      doc.text('Urun Indirimli Toplam:', 105, lineY);
      doc.text(`${formatPrice(discountedTotalAmount)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Genel İskonto
    if (hasIskonto) {
      doc.text(`Genel Iskonto Tutari (%${genelIskontoYuzde}):`, 105, lineY);
      doc.text(`-${formatPrice(totalAmount * gIskonto / 100)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;
      
      doc.text('Iskontolu Toplam:', 105, lineY);
      doc.text(`${formatPrice(iskontoluToplam)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Vade Farkı
    if (hasVade) {
      doc.text(`Vade Farki (%${vadeliYuzde}):`, 105, lineY);
      doc.text(`+${formatPrice(vadeFarkiTutari)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;

      doc.text('Vadeli Toplam:', 105, lineY);
      doc.text(`${formatPrice(vadeliToplam)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Ayırıcı çizgi (sadece detaylı görünümde ara toplamlar varsa)
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.3);
    doc.line(105, lineY - 2, 190, lineY - 2);
  }


  // Genel toplam
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('GENEL TOPLAM (Net):', 105, lineY + 6);
  doc.text(`${formatPrice(grandTotal)} TL`, 190, lineY + 6, { align: 'right' });
  lineY += 14;

  // Ödenen ve Kalan bilgileri (sadece Müşteri PDF için)
  if (showPaymentInfo) {
    // Ayırıcı çizgi
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.3);
    doc.line(105, lineY - 2, 190, lineY - 2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    // Ödenen
    doc.text('Odenen:', 105, lineY + 4);
    doc.text(`${formatPrice(paidAmount)} TL`, 190, lineY + 4, { align: 'right' });
    lineY += 9;

    // Kalan
    const isPaymentComplete = Math.abs(remainingAmount) < 0.01 || remainingAmount <= 0;
    doc.setFont('helvetica', 'bold');
    doc.text('Kalan:', 105, lineY + 4);
    if (isPaymentComplete) {
      doc.text('ODEME TAMAMLANDI', 190, lineY + 4, { align: 'right' });
    } else {
      doc.text(`${formatPrice(remainingAmount)} TL`, 190, lineY + 4, { align: 'right' });
    }
  }

  doc.setTextColor(0, 0, 0);
  return startY + boxHeight + 10;
};

// Müşteri PDF tablo verisini oluşturur
const buildCustomerTableData = (orders, includeMeasurements = true) => {
  const head = includeMeasurements
    ? [['Urun Tipi', 'Oda', 'Urun Adi', 'Olculer(cm)', 'Pile Modeli', 'Adet', 'Toplam Ucret (TL)', 'Indirimli (TL)']]
    : [['Urun Tipi', 'Oda', 'Urun Adi', 'Pile Modeli', 'Adet', 'Toplam Ucret (TL)', 'Indirimli (TL)']];

  const body = orders.flatMap((order) => {
    const cleanProductType = cleanTurkishChars(PRODUCT_TYPES[order.productType]?.label || order.productType || '');
    const cleanRoomName = cleanTurkishChars(order.roomName || '');
    const cleanPileModel = cleanTurkishChars(order.pileModel || '');
    const discountRate = parseFloat(order.discount) || 0;

    // Plise için özel satır bazlı genişletme
    if (order.productType === 'plise' && Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
      const p1Base = cleanTurkishChars(order.pliseProduct1Name || '');
      const p2Base = order.pliseProduct2Name ? ` + ${cleanTurkishChars(order.pliseProduct2Name)}` : '';

      return order.pliseItems.map((item) => {
        const ms = item.multisistem ? ' (Multisistem)' : '';
        const cleanProductName = item.multisistem ? (p1Base + p2Base + cleanTurkishChars(ms)) : p1Base;

        const row = [
          cleanProductType,
          cleanRoomName,
          cleanProductName
        ];

        if (includeMeasurements) {
          row.push(`${item.width || '-'} x ${item.height || '-'}`);
        }

        const rowTotal = parseFloat(item.rowTotal) || 0;
        const discountedRowTotal = rowTotal * (1 - discountRate / 100);

        row.push(
          '-', // Plise için pile modeli yok
          item.quantity || '1',
          formatPrice(rowTotal),
          discountRate > 0 ? formatPrice(discountedRowTotal) : '-'
        );

        return row;
      });
    }

    // Diğer ürün tipleri için tek satır
    let productName = order.productName || '';
    if (order.productVariant) productName += ` - ${order.productVariant}`;
    const cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');

    const row = [
      cleanProductType,
      cleanRoomName,
      cleanProductName
    ];

    if (includeMeasurements) {
      row.push(`${order.width || ''} x ${order.height || ''}`);
    }

    row.push(
      cleanPileModel,
      order.quantity || '1',
      formatPrice(order.totalPrice || 0),
      order.discountedPrice && parseFloat(order.discount) > 0 ? formatPrice(order.discountedPrice) : '-'
    );

    // Ray Sistemi için kullanılan metriği olculer sütununda göster (Müşteri PDF)
    if (order.productType === 'raySistemi' && includeMeasurements) {
      row[3] = `${order.width || ''} cm`; // Boy yok
      const usedMeter = order.usedMeter ? `${order.usedMeter} m` : '-';
      const olcu = order.olcuTamamlama ? ` (${order.olcuTamamlama})` : '';
      row[4] = cleanTurkishChars(order.olcuTamamlama || '-');
      row[3] = `${order.width || ''} cm\n${usedMeter}${olcu}`;
    }

    // Rustik için detayları göster
    if (order.productType === 'rustik' && includeMeasurements) {
      row[3] = `${order.width || ''} cm`; // Boy yok
      const usedCm = order.usedCm ? `${order.usedCm} cm` : '-';
      row[3] = `En: ${order.width || ''} cm\nKul: ${usedCm}`;

      let ayakDetay = cleanTurkishChars(order.ayakModel || '-');
      if (order.ayakSayisi) {
        ayakDetay += `\nAdet: ${order.ayakSayisi}`;
        if (order.ekAyakSayisi) ayakDetay += ` (+${order.ekAyakSayisi} Ek)`;
      }
      if (order.ayakCap || order.ayakRenkKodu) {
        ayakDetay += `\n(${order.ayakCap || ''} ${cleanTurkishChars(order.ayakRenkKodu || '')})`;
      }
      row[4] = ayakDetay;
    }

    return [row];
  });

  return { head, body };
};

// Ana Müşteri PDF oluşturma
const generateCustomerPDFBase = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, options = {}) => {
  const includeMeasurements = options.includeMeasurements !== false;
  const title = options.title || 'Musteri Siparisi';
  const showPaymentInfo = options.showPaymentInfo || false;
  const showDetailedPricing = options.showDetailedPricing !== false; // Varsayılan true
  const paidAmount = options.paidAmount || 0;
  const remainingAmount = options.remainingAmount || 0;

  if (!customerInfo || orders.length === 0) {
    alert('Müşteri bilgileri veya sipariş bulunamadı!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header
  drawHeader(doc, title);

  // Müşteri bilgileri
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);

  // Sipariş tablosu başlığı
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('SIPARIS DETAYLARI', 20, currentY);
  currentY += 8;

  // Tablo verilerini hazırla
  const { head, body } = buildCustomerTableData(orders, includeMeasurements);

  // Tablo oluştur
  autoTable(doc, {
    startY: currentY,
    head,
    body,
    theme: 'striped',
    headStyles: {
      fillColor: PRIMARY_COLOR,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center'
    },
    styles: {
      fontSize: 9,
      cellPadding: 4,
      halign: 'center'
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]
    },
    columnStyles: includeMeasurements
      ? {
        0: { cellWidth: 22 },  // Urun Tipi
        1: { cellWidth: 18 },  // Oda
        2: { cellWidth: 25 },  // Urun Adi
        3: { cellWidth: 20 },  // Olculer(cm)
        4: { cellWidth: 25 },  // Pile Modeli
        5: { cellWidth: 12, halign: 'center' },  // Adet
        6: { cellWidth: 28, halign: 'right' },   // Toplam Ucret
        7: { cellWidth: 25, halign: 'right' }    // Indirimli
      }
      : {
        0: { cellWidth: 25 },  // Urun Tipi
        1: { cellWidth: 20 },  // Oda
        2: { cellWidth: 30 },  // Urun Adi
        3: { cellWidth: 28 },  // Pile Modeli
        4: { cellWidth: 15, halign: 'center' },  // Adet
        5: { cellWidth: 28, halign: 'right' },   // Toplam Ucret
        6: { cellWidth: 25, halign: 'right' }    // Indirimli
      },
    pageBreak: 'auto',
    margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: function (data) {
      if (data.pageNumber > 1) {
        drawHeader(doc, title + ' (Devam)');
      }
    }
  });

  // Toplam tutar kutusu
  let finalY = doc.lastAutoTable.finalY + 10;

  // Toplam kutusunun tahmini yüksekliğini hesapla
  const hasVadeli = vadeliYuzde && parseFloat(vadeliYuzde) > 0;
  let estimatedBoxItems = 1; // Genel toplam
  if (showDetailedPricing) {
    estimatedBoxItems += 1; // Ara toplam
    if (discountedTotalAmount !== totalAmount) estimatedBoxItems++;
    if (vadeliYuzde && parseFloat(vadeliYuzde) > 0) estimatedBoxItems += 2;
    if (genelIskontoYuzde && parseFloat(genelIskontoYuzde) > 0) estimatedBoxItems += 2;
  }
  if (showPaymentInfo) estimatedBoxItems += 2;
  const estimatedBoxHeight = 20 + (estimatedBoxItems * 10);

  // Sayfa kontrolü - footer için 35px, kutu için tahmini yükseklik
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + estimatedBoxHeight > pageHeight - 40) {
    doc.addPage();
    drawHeader(doc, title);
    finalY = 50;
  }

  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, finalY, showPaymentInfo, paidAmount, remainingAmount, showDetailedPricing, customerInfo, orders);

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  // PDF'i yeni sekmede aç
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

// Müşteri PDF oluşturma
export const generateCustomerPDF = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, paidAmount = 0, remainingAmount = 0) => {
  return generateCustomerPDFBase(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, {
    includeMeasurements: true,
    title: 'Siparis Detayi',
    showPaymentInfo: true,
    showDetailedPricing: false, // Müşteri detayda detaylı fiyat gizle
    paidAmount,
    remainingAmount
  });
};

// Müşteri PDF Blob döndür (WhatsApp paylaşımı için)
export const generateCustomerPDFBlob = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, paidAmount = 0, remainingAmount = 0) => {
  if (!customerInfo || orders.length === 0) return null;
  const doc = new jsPDF();
  doc.setFont('helvetica');
  drawHeader(doc, 'Siparis Detayi');
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(...PRIMARY_COLOR);
  doc.text('SIPARIS DETAYLARI', 20, currentY); currentY += 8;
  const { head, body } = buildCustomerTableData(orders, true);
  autoTable(doc, {
    startY: currentY, head, body, theme: 'striped',
    headStyles: { fillColor: PRIMARY_COLOR, textColor: 255, fontStyle: 'bold', fontSize: 9, halign: 'center' },
    styles: { fontSize: 9, cellPadding: 4, halign: 'center' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 18 }, 2: { cellWidth: 25 }, 3: { cellWidth: 20 }, 4: { cellWidth: 25 }, 5: { cellWidth: 12, halign: 'center' }, 6: { cellWidth: 28, halign: 'right' }, 7: { cellWidth: 25, halign: 'right' } },
    pageBreak: 'auto', margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(doc, 'Siparis Detayi (Devam)'); }
  });
  let finalY = doc.lastAutoTable.finalY + 10;
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + 80 > pageHeight - 40) { doc.addPage(); drawHeader(doc, 'Siparis Detayi'); finalY = 50; }
  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, finalY, true, paidAmount, remainingAmount, false, customerInfo, orders);
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawFooter(doc); }
  return doc.output('blob');
};

// Müşteri (Sipariş öncesi) PDF oluşturma - en/boy bilgisi olmadan
export const generateCustomerPreOrderPDF = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde) => {
  return generateCustomerPDFBase(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, {
    includeMeasurements: false,
    title: 'Teklif Formu',
    showPaymentInfo: false,
    showDetailedPricing: true // Teklif formunda detaylı fiyat göster
  });
};

// Müşteri (Sipariş öncesi) PDF Blob döndür (WhatsApp paylaşımı için)
export const generateCustomerPreOrderPDFBlob = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde) => {
  if (!customerInfo || orders.length === 0) return null;
  const doc = new jsPDF();
  doc.setFont('helvetica');
  drawHeader(doc, 'Teklif Formu');
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(...PRIMARY_COLOR);
  doc.text('SIPARIS DETAYLARI', 20, currentY); currentY += 8;
  const { head, body } = buildCustomerTableData(orders, false);
  autoTable(doc, {
    startY: currentY, head, body, theme: 'striped',
    headStyles: { fillColor: PRIMARY_COLOR, textColor: 255, fontStyle: 'bold', fontSize: 9, halign: 'center' },
    styles: { fontSize: 9, cellPadding: 4, halign: 'center' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: { 0: { cellWidth: 25 }, 1: { cellWidth: 20 }, 2: { cellWidth: 30 }, 3: { cellWidth: 28 }, 4: { cellWidth: 15, halign: 'center' }, 5: { cellWidth: 28, halign: 'right' }, 6: { cellWidth: 25, halign: 'right' } },
    pageBreak: 'auto', margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(doc, 'Teklif Formu (Devam)'); }
  });
  let finalY = doc.lastAutoTable.finalY + 10;
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + 60 > pageHeight - 40) { doc.addPage(); drawHeader(doc, 'Teklif Formu'); finalY = 50; }
  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, finalY, false, 0, 0, true, customerInfo, orders);
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawFooter(doc); }
  return doc.output('blob');
};

// Atölye PDF oluşturma
export const generateWorkshopPDF = (customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Müşteri bilgileri veya sipariş bulunamadı!');
    return;
  }

  const tulKanatOrders = orders.filter(order =>
    order.productType === 'tul' || order.productType === 'kanat'
  );

  if (tulKanatOrders.length === 0) {
    alert('Atölye PDF için Tül veya Kanat ürünü bulunamadı!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header - Müşteri adını büyük başlık olarak yazdır (Talep üzerine)
  drawHeader(doc, 'Atolye Siparisi', customerInfo.name);

  // Siparişler için kutular
  const cleanCustomerName = cleanTurkishChars(customerInfo.name);
  let currentY = 45;
  let currentX = 12; // Daha geniş alan için kenardan başla
  const boxWidth = 90;
  const boxHeight = 63;
  const margin = 6; // Sütunlar arası boşluğu biraz daralt

  tulKanatOrders.forEach((order) => {
    if (currentY + boxHeight > 270) {
      doc.addPage();
      drawHeader(doc, 'Atolye Siparisi', customerInfo.name);
      currentY = 45;
      currentX = 12;
    }

    // Kutu arka planı
    doc.setFillColor(...LIGHT_BG);
    doc.roundedRect(currentX, currentY, boxWidth, boxHeight, 3, 3, 'F');

    // Kutu kenarlığı
    doc.setDrawColor(...PRIMARY_COLOR);
    doc.setLineWidth(0.5);
    doc.roundedRect(currentX, currentY, boxWidth, boxHeight, 3, 3, 'S');

    // Üst şerit - müşteri adı + oda adı
    doc.setFillColor(...PRIMARY_COLOR);
    doc.roundedRect(currentX, currentY, boxWidth, 14, 3, 3, 'F');
    doc.rect(currentX, currentY + 7, boxWidth, 7, 'F'); // Alt köşeleri düzelt

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);

    const cleanRoomName = cleanTurkishChars(order.roomName || '');
    // Sol: Müşteri Adı (Talep üzerine başlık), Sağ: Oda adı
    doc.text(cleanCustomerName.substring(0, 25), currentX + 4, currentY + 9);
    doc.setFontSize(8);
    doc.text(cleanRoomName, currentX + boxWidth - 4, currentY + 9, { align: 'right' });

    // İçerik
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);

    let fullProductName = order.productName || '';
    if (order.productVariant) fullProductName += ` - ${order.productVariant}`;
    const cleanProductName = cleanTurkishChars(fullProductName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');
    const cleanPileModel = cleanTurkishChars(order.pileModel || '');
    const cleanNotes = cleanTurkishChars(order.notes || '');

    doc.text(`Urun: ${cleanProductName.substring(0, 28)}`, currentX + 4, currentY + 20);
    doc.text(`Pile: ${cleanPileModel.substring(0, 25)}`, currentX + 4, currentY + 28);
    doc.setFont('helvetica', 'bold');
    doc.text(`En: ${order.width || '-'} cm  Boy: ${order.height || '-'} cm  Adet: ${order.quantity || '1'}`, currentX + 4, currentY + 36);

    // Not alanı
    if (cleanNotes) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY_TEXT);
      const noteLines = splitTextToLines(cleanNotes, 42);
      let noteY = currentY + 44;
      doc.text('Not:', currentX + 4, noteY);
      noteLines.slice(0, 2).forEach((line, i) => {
        doc.text(line, currentX + 4, noteY + 6 + (i * 5));
      });
    }

    doc.setTextColor(0, 0, 0);

    // Sonraki kutu pozisyonu
    currentX += boxWidth + margin;

    if (currentX + boxWidth > 200) {
      currentX = 12;
      currentY += boxHeight + 10; // Satır arası boşluk
    }
  });

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  // PDF'i yeni sekmede aç
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

// Montaj PDF oluşturma
export const generateInstallationPDF = (customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Müşteri bilgileri veya sipariş bulunamadı!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header
  drawHeader(doc, 'Montaj Siparisi');

  // Müşteri bilgileri kutusu
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);

  // Sipariş listesi başlığı
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('MONTAJ LISTESI', 20, currentY);
  currentY += 8;

  // Siparişleri oda gruplarına göre sırala
  const sortedOrders = [...orders].sort((a, b) => {
    const roomGroupA = (a.roomName || '').substring(0, 3).toUpperCase();
    const roomGroupB = (b.roomName || '').substring(0, 3).toUpperCase();

    if (roomGroupA !== roomGroupB) {
      return roomGroupA.localeCompare(roomGroupB, 'tr-TR');
    }

    const roomCompare = (a.roomName || '').localeCompare(b.roomName || '', 'tr-TR');
    if (roomCompare !== 0) return roomCompare;

    return (a.productType || '').localeCompare(b.productType || '', 'tr-TR');
  });

  // Tablo verileri
  const tableData = sortedOrders.flatMap((order) => {
    const cleanProductType = cleanTurkishChars(PRODUCT_TYPES[order.productType]?.label || order.productType || '');
    const cleanRoomName = cleanTurkishChars(order.roomName || '');

    // Plise için detaylı satır bazlı genişletme
    if (order.productType === 'plise' && Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
      const p1Base = cleanTurkishChars(order.pliseProduct1Name || '');
      const p2Base = order.pliseProduct2Name ? ` + ${cleanTurkishChars(order.pliseProduct2Name)}` : '';

      return order.pliseItems.map((item) => {
        const ms = item.multisistem ? ' (Multisistem)' : '';
        const namePart = item.multisistem ? (p1Base + p2Base + ms) : p1Base;
        const dimensionPart = ` [${item.width} x ${item.height}] - ${item.quantity} Adet`;
        const fullProductName = namePart + cleanTurkishChars(dimensionPart);

        return [
          cleanRoomName,
          cleanProductType,
          fullProductName
        ];
      });
    }

    // Diğer ürünler için
    let productName = order.productName || '';
    if (order.productVariant) productName += ` - ${order.productVariant}`;
    const cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');

    return [[
      cleanRoomName,
      cleanProductType,
      cleanProductName
    ]];
  });

  // Tablo oluştur
  autoTable(doc, {
    startY: currentY,
    head: [['Oda', 'Urun Tipi', 'Urun Adi']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: PRIMARY_COLOR,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 10
    },
    styles: {
      fontSize: 10,
      cellPadding: 5
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]
    },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 45 },
      2: { cellWidth: 75 }
    },
    margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: function (data) {
      if (data.pageNumber > 1) {
        drawHeader(doc, 'Montaj Siparisi (Devam)');
      }
    }
  });

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  // PDF'i yeni sekmede aç
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

// Firma PDF oluşturma
export const generateFirmaPDF = (customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Müşteri bilgileri veya sipariş bulunamadı!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header
  drawHeader(doc, 'Firma Siparisi');

  // Müşteri bilgileri kutusu
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);

  // Siparişleri ürün tipine göre ayır
  const tulKanatOrders = orders.filter(order => order.productType === 'tul' || order.productType === 'kanat');
  const otherOrders = orders.filter(order => !['tul', 'kanat'].includes(order.productType));

  // Tül ve Kanat tablosu
  if (tulKanatOrders.length > 0) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text('TUL VE KANAT SIPARISLERI', 20, currentY);

    const tulKanatTableData = tulKanatOrders.map((order) => {
      const cleanProductType = cleanTurkishChars(PRODUCT_TYPES[order.productType]?.label || order.productType || '');

      let productName = order.productName || '';
      if (order.productType === 'plise') {
        productName = order.pliseProduct1Name || '';
        if (order.pliseProduct2Name) {
          productName += ` + ${order.pliseProduct2Name}`;
        }
      } else if (order.productVariant) {
        productName += ` - ${order.productVariant}`;
      }

      const cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');

      return [
        cleanProductType,
        cleanProductName,
        `${order.usedM2 || ''} m`,
        order.quantity || '1'
      ];
    });

    autoTable(doc, {
      startY: currentY + 8,
      head: [['Urun Tipi', 'Urun Adi', 'Kullanilan m', 'Adet']],
      body: tulKanatTableData,
      theme: 'striped',
      headStyles: {
        fillColor: PRIMARY_COLOR,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      styles: {
        fontSize: 9,
        cellPadding: 4
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 60 },
        2: { cellWidth: 30, halign: 'center' },
        3: { cellWidth: 20, halign: 'center' }
      },
      margin: { top: 45, right: 15, bottom: 35, left: 15 },
      didDrawPage: function (data) {
        if (data.pageNumber > 1) {
          drawHeader(doc, 'Firma Siparisi (Devam)');
        }
      }
    });

    currentY = doc.lastAutoTable.finalY + 15;
  }

  // Diğer ürünler tablosu
  if (otherOrders.length > 0) {
    if (currentY > 200) {
      doc.addPage();
      drawHeader(doc, 'Firma Siparisi (Devam)');
      currentY = 50;
    }

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text('MEKANIK URUN SIPARISLERI', 20, currentY);

    const otherTableData = otherOrders.map((order) => {
      const cleanProductType = cleanTurkishChars(PRODUCT_TYPES[order.productType]?.label || order.productType || '');

      let productName = order.productName || '';
      if (order.productType === 'plise') {
        productName = order.pliseProduct1Name || '';
        if (order.pliseProduct2Name) {
          productName += ` + ${order.pliseProduct2Name}`;
        }
      } else if (order.productVariant) {
        productName += ` - ${order.productVariant}`;
      }

      const cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');

      let finalProductName = cleanProductName;
      if (order.productType === 'rustik') {
        let ayakDetay = ` [Ayak: ${cleanTurkishChars(order.ayakModel || '')}`;
        if (order.ayakRenkKodu) ayakDetay += ` - ${cleanTurkishChars(order.ayakRenkKodu)}`;
        ayakDetay += ` - ${order.ayakSayisi || 0} Adet`;
        if (order.ekAyakSayisi) ayakDetay += ` (+${order.ekAyakSayisi} Ek)`;
        ayakDetay += ']';
        finalProductName += ayakDetay;

        // Aksesuar ve Ray seçeneklerini de ekle
        if (Array.isArray(order.rustikAksesuarlar) && order.rustikAksesuarlar.length > 0) {
          finalProductName += '\n Aksesuarlar: ' + order.rustikAksesuarlar.map(a => `${cleanTurkishChars(a.name)} (${a.quantity})`).join(', ');
        }
        if (Array.isArray(order.rustikRaySecenekleri) && order.rustikRaySecenekleri.length > 0) {
          finalProductName += '\n Ray: ' + order.rustikRaySecenekleri.map(r => `${cleanTurkishChars(r.name)} (${r.quantity})`).join(', ');
        }
      }

      const isRay = order.productType === 'raySistemi';
      const isRustik = order.productType === 'rustik';

      const usedVal = isRay ? (order.usedMeter ? `${order.usedMeter} m` : '-')
        : isRustik ? (order.usedCm ? `${order.usedCm} cm` : '-')
          : (order.usedM2 ? `${order.usedM2} m2` : '-');

      const spec = isRay && order.olcuTamamlama ? `\n(${cleanTurkishChars(order.olcuTamamlama)})` : '';

      return [
        cleanProductType,
        finalProductName,
        `${order.width || ''}`,
        (isRay || isRustik) ? '-' : `${order.height || ''}`,
        `${usedVal}${spec}`,
        order.quantity || '1'
      ];
    });

    autoTable(doc, {
      startY: currentY + 8,
      head: [['Urun Tipi', 'Urun Adi', 'En (cm)', 'Boy (cm)', 'Kullanilan m2', 'Adet']],
      body: otherTableData,
      theme: 'striped',
      headStyles: {
        fillColor: PRIMARY_COLOR,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      styles: {
        fontSize: 9,
        cellPadding: 4
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 50 },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 20, halign: 'center' },
        4: { cellWidth: 25, halign: 'center' },
        5: { cellWidth: 18, halign: 'center' }
      },
      margin: { top: 45, right: 15, bottom: 35, left: 15 },
      didDrawPage: function (data) {
        if (data.pageNumber > 1) {
          drawHeader(doc, 'Firma Siparisi (Devam)');
        }
      }
    });
  }

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  // PDF'i yeni sekmede aç
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

const drawDealerInfoBox = (doc, dealer, dateRangeLabel, startY) => {
  const cleanCompany = cleanTurkishChars(dealer.companyName || '');
  const cleanName = cleanTurkishChars(dealer.name || '');
  const cleanAddress = cleanTurkishChars(dealer.address || '');
  const addressLines = splitTextToLines(cleanAddress, 60);
  const extraLines = Math.max(0, addressLines.length - 1);
  const boxHeight = 52 + (extraLines * 7);

  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(15, startY, 180, boxHeight, 3, 3, 'F');

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('BAYI BILGILERI', 20, startY + 10);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(`Firma: ${cleanCompany}`, 20, startY + 20);
  doc.text(`Yetkili: ${cleanName}`, 20, startY + 28);
  doc.text(`Telefon: ${dealer.phone1 || '-'}`, 20, startY + 36);

  let addressY = startY + 44;
  addressLines.forEach((line, index) => {
    const prefix = index === 0 ? 'Adres: ' : '       ';
    doc.text(`${prefix}${line}`, 20, addressY);
    addressY += 7;
  });

  doc.setFontSize(9);
  doc.setTextColor(...GRAY_TEXT);
  doc.text(`Tarih Araligi: ${cleanTurkishChars(dateRangeLabel)}`, 190, startY + 20, { align: 'right' });
  doc.text(`Rapor: ${new Date().toLocaleDateString('tr-TR')}`, 190, startY + 28, { align: 'right' });

  doc.setTextColor(0, 0, 0);
  return startY + boxHeight + 8;
};

const buildDealerAggregateTableData = (orders, sessionDateMap) => {
  const baseHead = buildCustomerTableData([], true).head[0];
  const head = [['Liste Tarihi', ...baseHead]];
  const body = [];

  orders.forEach((order) => {
    const sessionDate = sessionDateMap[order.sessionId] || '-';
    const { body: orderRows } = buildCustomerTableData([order], true);
    orderRows.forEach((row) => body.push([sessionDate, ...row]));
  });

  return { head, body };
};

const drawDealerSingleSummaryBox = (doc, title, subtitle, summaryData, startY) => {
  const {
    sessionCountLabel = 'Siparis Listesi Sayisi',
    sessionCount,
    orderCount,
    grandTotal,
    hasDirectPaid,
    sessionPaid,
    directPaid,
    grandPaid,
    isCreditor,
    isBalanced,
    creditBalance,
    openBalance,
    isBayiTedarikci,
    supplierSummary
  } = summaryData;

  const lines = [];
  if (sessionCount !== undefined) {
    lines.push({ label: sessionCountLabel, value: String(sessionCount || 0) });
  }
  if (orderCount !== undefined) {
    lines.push({ label: 'Toplam Urun / Kalem Adedi', value: String(orderCount || 0) });
  }
  if (grandTotal !== undefined) {
    lines.push({ label: 'Toplam Siparis Tutari', value: `${formatPrice(grandTotal || 0)} TL`, color: PRIMARY_COLOR, isBold: true });
  }
  if (hasDirectPaid) {
    if (sessionPaid !== undefined) {
      lines.push({ label: 'Siparis Ici Tahsilat', value: `${formatPrice(sessionPaid || 0)} TL`, color: [22, 101, 52] });
    }
    if (directPaid !== undefined) {
      lines.push({ label: 'Harici / Manuel Alinan Odeme', value: `${formatPrice(directPaid || 0)} TL`, color: [22, 101, 52] });
    }
  }
  if (grandPaid !== undefined) {
    lines.push({ label: 'Toplam Tahsil Edilen', value: `${formatPrice(grandPaid || 0)} TL`, color: [22, 101, 52], isBold: true });
  }
  if (isCreditor) {
    lines.push({ label: 'Alacakli (Fazla Odeme)', value: `${formatPrice(creditBalance || 0)} TL`, color: [180, 83, 9], isBold: true });
  } else if (isBalanced) {
    lines.push({ label: 'Kalan Bayi Bakiyesi', value: 'Dengede (0.00 TL)', color: [71, 85, 105], isBold: true });
  } else if (openBalance !== undefined) {
    lines.push({ label: 'Kalan Bayi Borcu (Bakiye)', value: `${formatPrice(openBalance || 0)} TL`, color: [220, 38, 38], isBold: true });
  }

  if (isBayiTedarikci && supplierSummary) {
    const totalRemainingTry = supplierSummary.totalRemainingTry || 0;
    const totalCreditTry = supplierSummary.totalCreditTry || 0;
    const netSupplier = supplierSummary.totalNetTry !== undefined ? supplierSummary.totalNetTry : (totalRemainingTry - totalCreditTry);
    lines.push({
      label: 'Tedarikci Kalan Borcumuz',
      value: `${formatPrice(totalRemainingTry)} TL`,
      color: totalRemainingTry > 0.01 ? [220, 38, 38] : [71, 85, 105],
      isBold: true
    });

    const netCariVal = (openBalance || 0) - (creditBalance || 0) - netSupplier;
    let netCariText = `${formatPrice(Math.abs(netCariVal))} TL (Dengede)`;
    let netColor = [71, 85, 105];
    if (netCariVal > 0.01) {
      netCariText = `${formatPrice(netCariVal)} TL (Bayi Borclu)`;
      netColor = [220, 38, 38];
    } else if (netCariVal < -0.01) {
      netCariText = `${formatPrice(Math.abs(netCariVal))} TL (Firma Alacakli)`;
      netColor = [180, 83, 9];
    }
    lines.push({ label: 'NET CARI BAKIYE DURUMU', value: netCariText, color: netColor, isBold: true });
  }

  const boxHeight = 18 + (lines.length * 8.5);
  const pageHeight = doc.internal.pageSize.height;

  let y = startY;
  if (y + boxHeight > pageHeight - 35) {
    doc.addPage();
    drawHeader(doc, 'Bayi Toplu Siparis Raporu (Devam)');
    y = 45;
  }

  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(15, y, 180, boxHeight, 3, 3, 'F');

  doc.setFontSize(10.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text(cleanTurkishChars(title), 20, y + 9);

  if (subtitle) {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY_TEXT);
    doc.text(cleanTurkishChars(subtitle), 190, y + 9, { align: 'right' });
  }

  let lineY = y + 19;
  lines.forEach((line) => {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', line.isBold ? 'bold' : 'normal');
    doc.setTextColor(...GRAY_TEXT);
    doc.text(cleanTurkishChars(line.label), 20, lineY);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...(line.color || [0, 0, 0]));
    doc.text(cleanTurkishChars(line.value), 190, lineY, { align: 'right' });
    lineY += 8.5;
  });

  doc.setTextColor(0, 0, 0);
  return y + boxHeight + 8;
};

const drawDealerAggregateSummaryBox = (
  doc,
  summary,
  dateRangeLabel,
  startY,
  overallSummary = null,
  isBayiTedarikci = false,
  supplierOverallSummary = null
) => {
  const isDateFiltered = dateRangeLabel && dateRangeLabel !== 'Tum Zamanlar';
  let currentY = startY;

  if (isDateFiltered && overallSummary) {
    // 1. Kutu: Seçilen Tarih Aralığı Özeti
    currentY = drawDealerSingleSummaryBox(
      doc,
      'SECILEN TARIH ARALIGI OZETI',
      dateRangeLabel,
      {
        sessionCountLabel: 'Tarih Araligi Liste Sayisi',
        sessionCount: summary.sessionCount,
        orderCount: summary.orderCount,
        grandTotal: summary.grandTotal,
        hasDirectPaid: summary.directPaid > 0,
        sessionPaid: summary.sessionPaid,
        directPaid: summary.directPaid,
        grandPaid: summary.grandPaid,
        isCreditor: summary.isCreditor,
        isBalanced: summary.isBalanced,
        creditBalance: summary.creditBalance,
        openBalance: summary.openBalance
      },
      currentY
    );

    // 2. Kutu: Genel Cari Bilgileri (Tüm Zamanlar)
    currentY = drawDealerSingleSummaryBox(
      doc,
      'GENEL CARI BILGILER (TUM ZAMANLAR)',
      'Tarih Araligindan Bagimsiz Genel Durum',
      {
        sessionCountLabel: 'Toplam Siparis Listesi Sayisi',
        sessionCount: overallSummary.sessionCount,
        grandTotal: overallSummary.grandTotal,
        hasDirectPaid: overallSummary.directPaid > 0,
        sessionPaid: overallSummary.sessionPaid,
        directPaid: overallSummary.directPaid,
        grandPaid: overallSummary.grandPaid,
        isCreditor: overallSummary.isCreditor,
        isBalanced: overallSummary.isBalanced,
        creditBalance: overallSummary.creditBalance,
        openBalance: overallSummary.openBalance,
        isBayiTedarikci,
        supplierSummary: supplierOverallSummary
      },
      currentY
    );
  } else {
    // Tarih filtresi yoksa tek kapsamlı özet kutusu
    currentY = drawDealerSingleSummaryBox(
      doc,
      'OZET BILGILER (TUM ZAMANLAR)',
      dateRangeLabel || 'Tum Zamanlar',
      {
        sessionCountLabel: 'Siparis Listesi Sayisi',
        sessionCount: summary.sessionCount,
        orderCount: summary.orderCount,
        grandTotal: summary.grandTotal,
        hasDirectPaid: summary.directPaid > 0,
        sessionPaid: summary.sessionPaid,
        directPaid: summary.directPaid,
        grandPaid: summary.grandPaid,
        isCreditor: summary.isCreditor,
        isBalanced: summary.isBalanced,
        creditBalance: summary.creditBalance,
        openBalance: summary.openBalance,
        isBayiTedarikci,
        supplierSummary: supplierOverallSummary
      },
      currentY
    );
  }

  return currentY;
};

export const generateDealerAggregatePDF = ({
  dealer,
  orders = [],
  summary,
  dateRangeLabel,
  sessionDateMap = {},
  overallSummary = null,
  isBayiTedarikci = false,
  supplierOverallSummary = null
}) => {
  if (!dealer || !summary?.sessionCount) {
    alert('Bayi bilgileri veya siparis listesi bulunamadi!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');
  const title = isBayiTedarikci ? 'Bayi / Tedarikci Toplu Siparis Raporu' : 'Bayi Toplu Siparis Raporu';

  drawHeader(doc, title, cleanTurkishChars(dealer.companyName || ''));

  let currentY = drawDealerInfoBox(doc, dealer, dateRangeLabel, 45);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('TUM SIPARISLER', 20, currentY);
  currentY += 8;

  if (orders.length === 0) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...GRAY_TEXT);
    doc.text('Secilen tarih araliginda kayitli urun siparisi bulunmuyor.', 20, currentY + 4);
    currentY += 16;
  } else {
    const { head, body } = buildDealerAggregateTableData(orders, sessionDateMap);

    autoTable(doc, {
      startY: currentY,
      head,
      body,
      theme: 'striped',
      headStyles: {
        fillColor: PRIMARY_COLOR,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      styles: {
        fontSize: 8,
        cellPadding: 3,
        halign: 'center',
        overflow: 'linebreak'
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: 20 },
        2: { cellWidth: 16 },
        3: { cellWidth: 24 },
        4: { cellWidth: 18 },
        5: { cellWidth: 22 },
        6: { cellWidth: 11, halign: 'center' },
        7: { cellWidth: 24, halign: 'right' },
        8: { cellWidth: 22, halign: 'right' }
      },
      pageBreak: 'auto',
      margin: { top: 45, right: 12, bottom: 35, left: 12 },
      didDrawPage(data) {
        if (data.pageNumber > 1) {
          drawHeader(doc, `${title} (Devam)`, cleanTurkishChars(dealer.companyName || ''));
        }
      }
    });

    currentY = doc.lastAutoTable.finalY + 10;
  }

  drawDealerAggregateSummaryBox(
    doc,
    summary,
    dateRangeLabel,
    currentY,
    overallSummary,
    isBayiTedarikci,
    supplierOverallSummary
  );

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

export const generateDealerDirectPaymentsPDF = ({
  dealer,
  payments = [],
  dateRangeLabel = 'Tum Zamanlar'
}) => {
  if (!dealer) {
    alert('Bayi bilgileri bulunamadi!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');
  const title = 'Harici Tahsilat ve Manuel Odemeler Raporu';

  drawHeader(doc, title, cleanTurkishChars(dealer.companyName || ''));

  let currentY = drawDealerInfoBox(doc, dealer, dateRangeLabel, 45);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('ALINAN HARICI ODEMELER', 20, currentY);
  currentY += 8;

  if (!payments || payments.length === 0) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...GRAY_TEXT);
    doc.text('Secilen tarih araliginda kayitli harici odeme bulunmuyor.', 20, currentY + 4);
    currentY += 16;
  } else {
    const head = [['Sira', 'Odeme Tarihi', 'Odeme Tipi', 'Aciklama / Not', 'Tutar']];
    const body = payments.map((p, index) => {
      const dateStr = p.date
        ? new Date(`${p.date}T00:00:00`).toLocaleDateString('tr-TR')
        : (p.createdAt ? new Date(p.createdAt).toLocaleDateString('tr-TR') : '—');
      return [
        String(index + 1),
        dateStr,
        cleanTurkishChars(p.type || 'Nakit'),
        cleanTurkishChars(p.notes || '—'),
        `${formatPrice(p.amount || 0)} TL`
      ];
    });

    autoTable(doc, {
      startY: currentY,
      head,
      body,
      theme: 'striped',
      headStyles: {
        fillColor: PRIMARY_COLOR,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      styles: {
        fontSize: 8,
        cellPadding: 3,
        halign: 'center',
        overflow: 'linebreak'
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: { cellWidth: 15, halign: 'center' },
        1: { cellWidth: 30, halign: 'center' },
        2: { cellWidth: 35, halign: 'center' },
        3: { cellWidth: 65, halign: 'left' },
        4: { cellWidth: 35, halign: 'right' }
      },
      pageBreak: 'auto',
      margin: { top: 45, right: 12, bottom: 35, left: 12 },
      didDrawPage(data) {
        if (data.pageNumber > 1) {
          drawHeader(doc, `${title} (Devam)`, cleanTurkishChars(dealer.companyName || ''));
        }
      }
    });

    currentY = doc.lastAutoTable.finalY + 10;
  }

  // Summary box for direct payments
  const totalPaid = payments.reduce((sum, p) => sum + (parseFloat(p.amount || 0) || 0), 0);
  const boxHeight = 38;
  const pageHeight = doc.internal.pageSize.height;

  if (currentY + boxHeight > pageHeight - 40) {
    doc.addPage();
    drawHeader(doc, `${title} (Devam)`);
    currentY = 50;
  }

  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(15, currentY, 180, boxHeight, 3, 3, 'F');

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('HARICI ODEME OZETI', 20, currentY + 10);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_TEXT);
  doc.text(cleanTurkishChars(dateRangeLabel), 190, currentY + 10, { align: 'right' });

  let lineY = currentY + 22;
  doc.text('Toplam Alinan Odeme Sayisi', 20, lineY);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(String(payments.length), 190, lineY, { align: 'right' });

  lineY += 8;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_TEXT);
  doc.text('Toplam Harici Tahsilat Tutari', 20, lineY);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(22, 101, 52);
  doc.text(`${formatPrice(totalPaid)} TL`, 190, lineY, { align: 'right' });

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

export const generateSupplierDirectPaymentsPDF = ({
  dealer,
  payments = [],
  dateRangeLabel = 'Tum Zamanlar'
}) => {
  if (!dealer) {
    alert('Tedarikci bilgileri bulunamadi!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');
  const title = 'Tedarikciye Yapilan Harici Odemeler Raporu';

  drawHeader(doc, title, cleanTurkishChars(dealer.companyName || ''));

  let currentY = drawDealerInfoBox(doc, dealer, dateRangeLabel, 45);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('YAPILAN HARICI ODEMELER', 20, currentY);
  currentY += 8;

  if (!payments || payments.length === 0) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...GRAY_TEXT);
    doc.text('Secilen tarih araliginda kayitli harici odeme bulunmuyor.', 20, currentY + 4);
    currentY += 16;
  } else {
    const head = [['Sira', 'Odeme Tarihi', 'Kur', 'Odeme Tipi', 'Aciklama / Not', 'Tutar']];
    const body = payments.map((p, index) => {
      const dateStr = p.date
        ? new Date(`${p.date}T00:00:00`).toLocaleDateString('tr-TR')
        : (p.createdAt ? new Date(p.createdAt).toLocaleDateString('tr-TR') : '—');
      const cur = p.currency || 'TRY';
      const curSymbol = cur === 'TRY' ? 'TL' : cur;
      return [
        String(index + 1),
        dateStr,
        cur,
        cleanTurkishChars(p.type || 'Nakit'),
        cleanTurkishChars(p.notes || '—'),
        `${formatPrice(p.amount || 0)} ${curSymbol}`
      ];
    });

    autoTable(doc, {
      startY: currentY,
      head,
      body,
      theme: 'striped',
      headStyles: {
        fillColor: [109, 40, 217], // Purple for supplier
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      styles: {
        fontSize: 8,
        cellPadding: 3,
        halign: 'center',
        overflow: 'linebreak'
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' },
        1: { cellWidth: 28, halign: 'center' },
        2: { cellWidth: 18, halign: 'center' },
        3: { cellWidth: 32, halign: 'center' },
        4: { cellWidth: 60, halign: 'left' },
        5: { cellWidth: 30, halign: 'right' }
      },
      pageBreak: 'auto',
      margin: { top: 45, right: 12, bottom: 35, left: 12 },
      didDrawPage(data) {
        if (data.pageNumber > 1) {
          drawHeader(doc, `${title} (Devam)`, cleanTurkishChars(dealer.companyName || ''));
        }
      }
    });

    currentY = doc.lastAutoTable.finalY + 10;
  }

  // Summary box for direct payments
  const currencyTotals = payments.reduce((acc, p) => {
    const cur = p.currency || 'TRY';
    acc[cur] = (acc[cur] || 0) + (parseFloat(p.amount || 0) || 0);
    return acc;
  }, {});

  const currencyKeys = Object.keys(currencyTotals);
  const extraLines = Math.max(1, currencyKeys.length);
  const boxHeight = 26 + (extraLines * 8);
  const pageHeight = doc.internal.pageSize.height;

  if (currentY + boxHeight > pageHeight - 40) {
    doc.addPage();
    drawHeader(doc, `${title} (Devam)`);
    currentY = 50;
  }

  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(15, currentY, 180, boxHeight, 3, 3, 'F');

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(109, 40, 217);
  doc.text('HARICI ODEME OZETI', 20, currentY + 10);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_TEXT);
  doc.text(cleanTurkishChars(dateRangeLabel), 190, currentY + 10, { align: 'right' });

  let lineY = currentY + 20;
  doc.text('Toplam Yapilan Odeme Sayisi', 20, lineY);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(String(payments.length), 190, lineY, { align: 'right' });

  if (currencyKeys.length === 0) {
    lineY += 8;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY_TEXT);
    doc.text('Toplam Odenen Tutar', 20, lineY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(22, 101, 52);
    doc.text('0,00 TL', 190, lineY, { align: 'right' });
  } else {
    currencyKeys.forEach((cur) => {
      lineY += 8;
      const curSymbol = cur === 'TRY' ? 'TL' : cur;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY_TEXT);
      doc.text(`Toplam Odenen (${cur})`, 20, lineY);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(22, 101, 52);
      doc.text(`${formatPrice(currencyTotals[cur])} ${curSymbol}`, 190, lineY, { align: 'right' });
    });
  }

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};
